import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Instagram, Package, Undo2, Mail, Phone, MapPin, BookOpen, Lock, ArrowUpRight, Search, ChevronLeft, ChevronRight, Download, FileText, FileSpreadsheet, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";

const formatMoneyOrFree = (amount, currency) =>
  Number(amount || 0) === 0 ? "Free" : formatMoney(amount, currency);
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const STATE_COLORS = {
  paid: "bg-mint text-ink",
  pending_payment: "bg-peach text-ink",
  pending_manual_verification: "bg-lilac text-ink",
  awaiting_manual_payment: "bg-peach text-ink",
  link_failed: "bg-hotpink text-white",
  requested: "bg-peach text-ink",
  approved: "bg-mint text-ink",
  rejected: "bg-hotpink text-white",
  return_rejected: "bg-hotpink text-white",
  returned: "bg-mint text-ink",
  partially_returned: "bg-peach text-ink",
  exchange_requested: "bg-[#4C1D95] text-white",
  exchanged: "bg-mint text-ink",
  exchange_rejected: "bg-hotpink text-white",
};

// Kept in sync with Dashboard.jsx's STATE_MEANINGS for the order/return
// keys that appear here (this sheet never shows chat states).
const STATE_MEANINGS = {
  paid: "Payment received and confirmed. This is a completed sale.",
  pending_payment: "The order exists but the customer hasn't paid yet.",
  pending_manual_verification: "Customer says they paid manually — needs your Confirm/Reject.",
  awaiting_manual_payment: "Waiting for the customer to complete a manual payment (UPI ID or bank transfer).",
  link_failed: "Razorpay payment link failed to generate — needs attention.",
  requested: "Customer requested a return — needs your decision.",
  approved: "You approved this return. Stock is restocked automatically.",
  rejected: "This order was rejected — usually because a manually-reported payment couldn't be verified.",
  return_rejected: "You declined this return request. The customer was notified, along with your return policy.",
  returned: "This order was fully returned and refunded.",
  partially_returned: "Some items from this order were returned and refunded — the rest of the order stands.",
  exchange_requested: "Customer asked to swap an item for a different size — needs your decision.",
  exchanged: "An item on this order was swapped for a different size and the stock was updated.",
  exchange_rejected: "You declined an exchange request on this order. The customer was notified.",
};

function Badge({ state }) {
  const [open, setOpen] = useState(false);
  const meaning = STATE_MEANINGS[state] || "No description yet for this state.";
  return (
    <span className="group relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={meaning}
        className={`cursor-help rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${STATE_COLORS[state] || "bg-ink/10 text-ink"}`}
      >
        {(state || "").replace(/_/g, " ")}
      </button>
      <span
        className={`absolute left-0 top-full z-20 mt-1 w-56 rounded-xl border border-ink/10 bg-white p-2.5 text-left text-[11px] font-normal normal-case leading-snug text-ink shadow-lg ${
          open ? "block" : "hidden md:group-hover:block"
        }`}
      >
        {meaning}
        <Link
          to="/status-guide"
          onClick={(e) => e.stopPropagation()}
          className="mt-1.5 flex items-center gap-1 text-[10px] font-bold text-hotpink hover:underline"
        >
          <BookOpen size={10} /> See all statuses →
        </Link>
      </span>
    </span>
  );
}

function PartialTag() {
  return (
    <span className="rounded-full bg-lilac px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ink">
      Partial return
    </span>
  );
}

// Compact pager for a side-sheet section — same Prev/Next shape as the
// dashboard's <Pager>, just smaller and without the page-of-total label
// needing a full row of its own.
function MiniPager({ page, total, limit, onPage, testPrefix }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (pages <= 1) return null;
  return (
    <div className="mt-3 flex items-center justify-between text-xs text-ink/60">
      <span data-testid={`${testPrefix}-page-info`}>
        Page {page} of {pages} · {total} total
      </span>
      <div className="flex items-center gap-1.5">
        <button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          data-testid={`${testPrefix}-prev`}
          className="inline-flex items-center gap-0.5 rounded-full border border-ink/15 bg-white px-2 py-1 disabled:opacity-40"
        >
          <ChevronLeft size={12} /> Prev
        </button>
        <button
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          data-testid={`${testPrefix}-next`}
          className="inline-flex items-center gap-0.5 rounded-full border border-ink/15 bg-white px-2 py-1 disabled:opacity-40"
        >
          Next <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}

// Same CSV/XLSX download pattern as the main dashboard's ListToolbar
// (authenticated blob fetch, filename from Content-Disposition), just
// scoped to one customer via customer_ig_id + this sheet's own search box.
function HistoryExportMenu({ canExport, onDownload, label }) {
  if (!canExport) {
    return (
      <a
        href="/plans"
        title="Export is available on Growth and Pro plans"
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-ink/15 bg-white px-2.5 py-1 text-xs font-semibold text-ink/50"
      >
        <Lock size={11} /> Export
      </a>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`history-${label}-export-menu`}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink px-2.5 py-1 text-xs font-semibold text-cream hover:bg-ink/85"
        >
          <Download size={11} /> Export <ChevronDown size={11} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuItem onClick={() => onDownload("csv")} className="cursor-pointer">
          <FileText size={14} className="mr-2" /> Download CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDownload("xlsx")} className="cursor-pointer">
          <FileSpreadsheet size={14} className="mr-2" /> Download Excel (.xlsx)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OrderAmountBreakdown({ o, currency }) {
  const [open, setOpen] = useState(false);
  const discountAmount = Number(o.discount_amount || 0);
  const shippingFee = Number(o.shipping_fee_charged || 0);
  const hasDiscount = discountAmount > 0;
  const hasShipping = shippingFee > 0;
  const hasBreakdown = hasDiscount || hasShipping;
  const subtotal = Number(o.total_amount || 0) + discountAmount - shippingFee;

  return (
    <div>
      <p className="mt-1 font-display text-lg font-black">{formatMoneyOrFree(o.total_amount, currency)}</p>
      {hasBreakdown && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-0.5 text-[10px] font-semibold text-hotpink underline decoration-dotted"
          >
            {open ? "Hide breakup" : "View breakup"}
          </button>
          {open && (
            <div className="mt-1.5 space-y-1 rounded-lg bg-ink/[0.03] px-2.5 py-2 text-[11px] text-ink/70">
              <div className="flex items-center justify-between gap-4">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal, currency)}</span>
              </div>
              {hasDiscount && (
                <div className="flex items-center justify-between gap-4 text-hotpink">
                  <span>Discount{o.discount_code ? ` (${o.discount_code})` : ""}</span>
                  <span>−{formatMoney(discountAmount, currency)}</span>
                </div>
              )}
              {hasShipping && (
                <div className="flex items-center justify-between gap-4">
                  <span>Shipping</span>
                  <span>+{formatMoney(shippingFee, currency)}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4 border-t border-ink/10 pt-1 font-semibold text-ink">
                <span>Total</span>
                <span>{formatMoneyOrFree(o.total_amount, currency)}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function dedupeById(arr, keyFn = (item) => item?.id) {
  const seen = new Set();
  return arr.filter((item) => {
    const key = keyFn(item);
    if (key == null || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const PAGE_LIMIT = 5;
// Fetched in one network call and paged through client-side. 20 is the
// backend's existing max (Extract Request Data already clamps orders_limit/
// returns_limit to 20) — no workflow change needed to raise it.
const FETCH_LIMIT = 20;
const PAGES_PER_BATCH = FETCH_LIMIT / PAGE_LIMIT; // 4 display-pages per fetch

export default function CustomerHistorySheet({ open, onOpenChange, customerIgId, currency, plan }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const isFree = plan === "free" || plan === "creator";
  // Same export gate as every other export on the dashboard (Growth/Pro) --
  // Starter can see History but not export it, matching the main tabs.
  const canExport = plan === "growth" || plan === "pro";

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [ordersPage, setOrdersPage] = useState(1);
  const [returnsPage, setReturnsPage] = useState(1);

  // Which 20-row batch each display page falls in (1-indexed). Prev/Next
  // inside an already-loaded batch just changes this number's remainder,
  // not its value — React's dependency check on the effect below only
  // re-fires when the *batch* actually changes.
  const ordersBatch = Math.ceil(ordersPage / PAGES_PER_BATCH);
  const returnsBatch = Math.ceil(returnsPage / PAGES_PER_BATCH);

  // Reset local UI state whenever a different customer's sheet is opened,
  // so a stale search/page from the last customer never leaks into the next.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setQuery("");
    setOrdersPage(1);
    setReturnsPage(1);
  }, [open, customerIgId]);

  useEffect(() => {
    if (!open || !customerIgId || isFree) return;
    setLoading(true);
    const params = new URLSearchParams({
      id: customerIgId,
      orders_page: String(ordersBatch),
      orders_limit: String(FETCH_LIMIT),
      returns_page: String(returnsBatch),
      returns_limit: String(FETCH_LIMIT),
    });
    if (query) params.set("search", query);
    api
      .get(`/merchant/customers/detail?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [open, customerIgId, isFree, query, ordersBatch, returnsBatch]);

  const applySearch = (e) => {
    e.preventDefault();
    // A new search reshuffles both lists, so both need to restart at page 1
    // — otherwise a page-3 offset from the old result set could land past
    // the end of the new, smaller one.
    setOrdersPage(1);
    setReturnsPage(1);
    setQuery(search.trim());
  };

  // Reuses the existing orders/returns export endpoints -- just scoped down
  // to this one customer via customer_ig_id, and respecting whatever order-#
  // search is currently active in this sheet, same as the main dashboard
  // exports respect their own search box.
  const downloadHistoryExport = async (kind, format) => {
    try {
      const path = kind === "orders" ? "/merchant/orders/export" : "/merchant/returns/export";
      const params = new URLSearchParams({ customer_ig_id: customerIgId });
      if (query) params.set("search", query);
      const res = await api.get(`${path}/${format}?${params.toString()}`, { responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `${kind}.${format}`;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Downloading ${kind} ${format.toUpperCase()}…`);
    } catch (e) {
      toast.error(`Couldn't download ${kind} ${format.toUpperCase()} — try again.`);
    }
  };

  if (isFree) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto bg-cream sm:max-w-xl"
          data-testid="customer-history-sheet-locked"
        >
          <SheetHeader>
            <SheetTitle className="font-display text-2xl tracking-tight">
              Customer relationship
            </SheetTitle>
            <SheetDescription>
              Every order and return this customer has ever had with you.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-8 flex flex-col items-center rounded-3xl border border-ink/10 bg-white p-8 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
              <Lock size={20} />
            </span>
            <h3 className="mt-4 font-display text-xl font-black tracking-tight">
              Locked on your current plan.
            </h3>
            <p className="mt-2 max-w-sm text-sm text-ink/60">
              Customer history — every past order and return for this customer — is available from the Starter
              plan onwards. You're on {plan === "creator" ? "Creator" : "Free"} — upgrade to unlock.
            </p>
            <a
              href="/plans"
              data-testid="history-upgrade-cta"
              className="cta-glow mt-5 inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-sm font-semibold text-white"
            >
              Upgrade to Starter <ArrowUpRight size={14} />
            </a>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  const c = data?.customer;
  // The API now returns up to 20 rows (the current batch) — slice out just
  // this display page's 5 from it. indexInBatch is 0-3 (this page's
  // position within its batch of 4 pages).
  const ordersBatchRows = dedupeById(data?.orders || []);
  // Returns batch now mixes return_requests + exchange_requests rows (see
  // the backend's combined query) -- their ids can collide across the two
  // tables, so key on row_key ("return:5" / "exchange:5") instead of the
  // raw numeric id.
  const returnsBatchRows = dedupeById(data?.returns || [], (r) => r.row_key || `${r.kind || "return"}:${r.id}`);
  const ordersIndexInBatch = (ordersPage - 1) % PAGES_PER_BATCH;
  const returnsIndexInBatch = (returnsPage - 1) % PAGES_PER_BATCH;
  const orders = ordersBatchRows.slice(ordersIndexInBatch * PAGE_LIMIT, ordersIndexInBatch * PAGE_LIMIT + PAGE_LIMIT);
  const returns = returnsBatchRows.slice(returnsIndexInBatch * PAGE_LIMIT, returnsIndexInBatch * PAGE_LIMIT + PAGE_LIMIT);
  const ordersTotal = data?.orders_total ?? orders.length;
  const returnsTotal = data?.returns_total ?? returns.length;
  // True lifetime spend now comes from the backend (customer.total_spent) —
  // summing `orders` here would only total whichever 5 rows are on screen,
  // which silently broke once pagination landed (orders used to be the
  // customer's full order list; now it's one page of it).
  const totalSpent = Number(c?.total_spent || 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto bg-cream sm:max-w-xl"
        data-testid="customer-history-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl tracking-tight">
            Customer relationship
          </SheetTitle>
          <SheetDescription>
            Every order and return this customer has ever had with you.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={applySearch} className="relative mt-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order #…"
            data-testid="history-search-input"
            className="w-full rounded-full border border-ink/15 bg-white py-2 pl-9 pr-20 text-sm focus:border-hotpink focus:outline-none"
          />
          <button
            type="submit"
            data-testid="history-search-btn"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-cream hover:bg-ink/85"
          >
            Search
          </button>
        </form>
        {query && (
          <p className="mt-2 text-xs text-ink/50">
            Showing orders and returns matching "{query}" —{" "}
            <button
              type="button"
              onClick={() => { setSearch(""); setQuery(""); setOrdersPage(1); setReturnsPage(1); }}
              className="font-semibold text-hotpink underline decoration-dotted"
            >
              clear
            </button>
          </p>
        )}

        {loading || !data ? (
          <div className="mt-10 flex items-center justify-center">
            <Loader2 className="animate-spin text-hotpink" />
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {/* Customer card */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5">
              <div className="flex items-center gap-3">
                {c?.profile_pic ? (
                  <img
                    src={c.profile_pic}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-sunset-gradient text-white">
                    <Instagram size={16} />
                  </span>
                )}
                <div>
                  {c?.instagram_profile_url ? (
                    <a
                      href={c.instagram_profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-display text-lg font-black tracking-tight text-hotpink hover:underline"
                      title={`Open @${c.customer_ig_handle} on Instagram`}
                    >
                      {c.display_name || "Unknown"}
                    </a>
                  ) : (
                    <p className="font-display text-lg font-black tracking-tight">
                      {c?.display_name || "Unknown"}
                    </p>
                  )}
                  {c?.created_at && (
                    <p className="text-xs text-ink/50">Customer since {new Date(c.created_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
              <div className="mt-4 grid gap-2 text-sm text-ink/70">
                <p className="flex items-center gap-2"><Phone size={14} className="text-ink/40" /> {c?.customer_phone || "—"}</p>
                <p className="flex items-center gap-2"><Mail size={14} className="text-ink/40" /> {c?.customer_email || "—"}</p>
                <p className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 text-ink/40" /> {c?.address_str || "—"}</p>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-2xl bg-ink/5 p-3">
                  <p className="text-xs uppercase tracking-[0.15em] text-ink/60">Orders</p>
                  <p className="font-display text-xl font-black">{ordersTotal}</p>
                </div>
                <div className="rounded-2xl bg-mint/60 p-3">
                  <p className="text-xs uppercase tracking-[0.15em] text-ink/60">Spent</p>
                  <p className="font-display text-xl font-black">{formatMoney(totalSpent, currency)}</p>
                </div>
                <div className="rounded-2xl bg-peach/60 p-3">
                  <p className="text-xs uppercase tracking-[0.15em] text-ink/60">Returns</p>
                  <p className="font-display text-xl font-black">{returnsTotal}</p>
                </div>
              </div>
            </div>

            {/* Orders */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="history-orders">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-hotpink" />
                  <p className="font-display text-lg font-black tracking-tight">Orders</p>
                </div>
                <HistoryExportMenu canExport={canExport} onDownload={(fmt) => downloadHistoryExport("orders", fmt)} label="orders" />
              </div>
              {orders.length === 0 ? (
                <p className="mt-3 text-sm text-ink/50">{query ? "No orders match that search." : "No orders yet."}</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {orders.map((o) => (
                    <li key={o.id} className="rounded-2xl border border-ink/10 p-4">
                      <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-xs text-ink/60">{o.order_number || String(o.id ?? "").slice(0, 8)}</p>
                        <OrderAmountBreakdown o={o} currency={currency} />
                      </div>
                        <div className="flex flex-col items-end gap-1.5">
                          <Badge state={o.status} />
                          {o.return_status && <Badge state={o.return_status} />}
                          {o.exchange_status && <Badge state={o.exchange_status} />}
                        </div>
                      </div>
                      {o.items?.length > 0 && (
                        <ul className="mt-3 space-y-1 text-sm text-ink/70">
                          {o.items.map((it) => (
                            <li key={it.id} className="flex items-start justify-between">
                              <span>
                                {it.product_name}{it.size ? ` · ${it.size}` : ""} × {it.quantity}
                                {Number(it.quantity || 1) > 1 && (
                                  <span className="block text-xs text-ink/40">{formatMoneyOrFree(it.unit_price, currency)} each</span>
                                )}
                                {it.exchange_request_status && (
                                  <span className="block text-xs font-semibold text-[#4C1D95]">
                                    ⇄ {it.exchange_request_status === "approved" ? "Exchanged" : it.exchange_request_status === "rejected" ? "Exchange rejected" : "Exchange requested"}
                                    {it.exchange_to_size ? ` (${it.exchange_from_size ? `${it.exchange_from_size} → ` : ""}${it.exchange_to_size})` : ""}
                                  </span>
                                )}
                              </span>
                              <span className="font-mono text-xs">{formatMoneyOrFree(Number(it.unit_price || 0) * Number(it.quantity || 1), currency)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-3 text-xs text-ink/50">{new Date(o.created_at).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              )}
              <MiniPager page={ordersPage} total={ordersTotal} limit={PAGE_LIMIT} onPage={setOrdersPage} testPrefix="history-orders" />
            </div>

            {/* Returns */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="history-returns">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Undo2 size={16} className="text-hotpink" />
                  <p className="font-display text-lg font-black tracking-tight">Return requests</p>
                </div>
                <HistoryExportMenu canExport={canExport} onDownload={(fmt) => downloadHistoryExport("returns", fmt)} label="returns" />
              </div>
              {returns.length === 0 ? (
                <p className="mt-3 text-sm text-ink/50">{query ? "No return requests match that search." : "No return requests."}</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {returns.map((r) => {
                    const isExchange = r.kind === "exchange";
                    const statusState = isExchange
                      ? { requested: "exchange_requested", approved: "exchanged", rejected: "exchange_rejected" }[r.status] || r.status
                      : r.status === "rejected" ? "return_rejected" : r.status;
                    return (
                      <li key={r.row_key || `${r.kind || "return"}:${r.id}`} className="rounded-2xl border border-ink/10 p-4">
                        <div className="flex items-center justify-between">
                          <p className="font-mono text-xs text-ink/60">{r.order_number || String(r.order_id ?? "").slice(0, 8)}</p>
                          <div className="flex items-center gap-1.5">
                            {isExchange && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#4C1D95] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                ⇄ Exchange
                              </span>
                            )}
                            {!isExchange && r.is_partial && <PartialTag />}
                            <Badge state={statusState} />
                          </div>
                        </div>
                        {isExchange ? (
                          <p className="mt-2 text-sm text-ink/70">
                            {(r.return_items && r.return_items[0]?.product_name) || "Item"}
                            {r.from_size ? ` · size ${r.from_size} → ${r.to_size}` : ""}
                          </p>
                        ) : (
                          <>
                            <p className="mt-2 text-sm text-ink/70">{r.reason}</p>
                            {r.return_items?.length > 0 && (
                              <ul className="mt-2 space-y-0.5 text-xs text-ink/60">
                                {r.return_items.map((it, idx) => (
                                  <li key={idx}>
                                    {it.product_name}{it.size ? ` · ${it.size}` : ""} × {it.quantity}
                                    {it.ordered_quantity && it.ordered_quantity !== it.quantity && (
                                      <span className="text-ink/40"> (of {it.ordered_quantity} ordered)</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}
                        <p className="mt-2 text-xs text-ink/50">{new Date(r.created_at).toLocaleString()}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
              <MiniPager page={returnsPage} total={returnsTotal} limit={PAGE_LIMIT} onPage={setReturnsPage} testPrefix="history-returns" />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
